'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

import { withFilter } from '../filters';
import type { OperationFilters } from '../types';

interface FilterPanelProps {
  filters: OperationFilters;
  onFiltersChange: (filters: OperationFilters) => void;
  options?: {
    stores: Array<{ id: string; name: string }>;
    processors: Array<{ id: string; name: string; type: string }>;
    statuses: string[];
    paymentTypes: string[];
    failureReasons: string[];
  };
}

const STATUS_KEYS = ['active', 'trialing', 'past_due', 'paused', 'canceled'] as const;

const VAULT_KEYS = ['tagada', 'external', 'null'] as const;

const CYCLE_OPTIONS = [
  { value: 0, label: '0' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 'above3', label: '>3' },
];

export function FilterPanel({
  filters,
  onFiltersChange,
  options,
}: FilterPanelProps) {
  const { t } = useTranslation('subscriptions');
  const statusOptions = React.useMemo(
    () =>
      STATUS_KEYS.map((key) => ({
        value: key,
        label: t(`filters.statuses.${key}`),
      })),
    [t]
  );
  const vaultOptions = React.useMemo(
    () =>
      VAULT_KEYS.map((key) => ({
        value: key,
        label: t(`filters.vaultOptions.${key}`),
      })),
    [t]
  );

  const toggleArrayValue = (key: keyof OperationFilters, value: string) => {
    const current = (filters[key] as string[] | undefined) || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onFiltersChange(withFilter(filters, key, updated as OperationFilters[typeof key]));
  };

  const [searchInput, setSearchInput] = React.useState(filters.search || '');
  const filtersRef = React.useRef(filters);
  filtersRef.current = filters;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (filtersRef.current.search !== (searchInput || undefined)) {
        onFiltersChange(withFilter(filtersRef.current, 'search', searchInput || undefined));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, onFiltersChange]);

  // Get selected count for a filter
  const getSelectedCount = (key: keyof OperationFilters) => {
    const value = filters[key];
    if (Array.isArray(value)) return value.length;
    if (value !== undefined) return 1;
    return 0;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <Label className="text-xs font-medium uppercase text-muted-foreground">
          {t('filters.title')}
        </Label>
        <p className="text-xs text-muted-foreground mt-1">
          {t('filters.description')}
        </p>
      </div>

      {/* Search */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('filters.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {/* Accordion Filters */}
      <Accordion type="multiple" defaultValue={['status']} className="w-full">
        {/* Status */}
        <AccordionItem value="status">
          <AccordionTrigger className="py-3 text-sm">
            <div className="flex items-center gap-2">
                {t('filters.status')}
              {getSelectedCount('statuses') > 0 && (
                <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  {getSelectedCount('statuses')}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {statusOptions.map((status) => (
                <label
                  key={status.value}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <Checkbox
                    checked={filters.statuses?.includes(status.value) || false}
                    onCheckedChange={() => toggleArrayValue('statuses', status.value)}
                  />
                  <span className="text-sm">{status.label}</span>
                </label>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Store */}
        {options?.stores && options.stores.length > 0 && (
          <AccordionItem value="store">
            <AccordionTrigger className="py-3 text-sm">
              <div className="flex items-center gap-2">
                {t('filters.store')}
                {getSelectedCount('storeIds') > 0 && (
                  <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                    {getSelectedCount('storeIds')}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {options.stores.map((store) => (
                  <label
                    key={store.id}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <Checkbox
                      checked={filters.storeIds?.includes(store.id) || false}
                      onCheckedChange={() => toggleArrayValue('storeIds', store.id)}
                    />
                    <span className="text-sm" title={store.name}>
                      {store.name}
                    </span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Processor */}
        {options?.processors && options.processors.length > 0 && (
          <AccordionItem value="processor">
            <AccordionTrigger className="py-3 text-sm">
              <div className="flex items-center gap-2">
                {t('filters.processor')}
                {getSelectedCount('processorIds') > 0 && (
                  <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                    {getSelectedCount('processorIds')}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {options.processors.map((processor) => (
                  <label
                    key={processor.id}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <Checkbox
                      checked={filters.processorIds?.includes(processor.id) || false}
                      onCheckedChange={() => toggleArrayValue('processorIds', processor.id)}
                    />
                    <span className="text-sm" title={processor.name}>
                      {processor.name}
                    </span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Vault Type */}
        <AccordionItem value="vault">
          <AccordionTrigger className="py-3 text-sm">
            <div className="flex items-center gap-2">
              {t('filters.vaultType')}
              {getSelectedCount('vaultType') > 0 && (
                <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  {getSelectedCount('vaultType')}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {vaultOptions.map((vault) => (
                <label
                  key={vault.value}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <Checkbox
                    checked={filters.vaultType?.includes(vault.value as any) || false}
                    onCheckedChange={() => toggleArrayValue('vaultType', vault.value)}
                  />
                  <span className="text-sm">{vault.label}</span>
                </label>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Scheduled to Cancel */}
        <AccordionItem value="cancelAtPeriodEnd">
          <AccordionTrigger className="py-3 text-sm">
            <div className="flex items-center gap-2">
              {t('filters.cancelAtPeriodEnd')}
              {filters.cancelAtPeriodEnd !== undefined && (
                <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  1
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {([true, false] as const).map((value) => (
                <label key={String(value)} className="flex items-center space-x-2 cursor-pointer">
                  <Checkbox
                    checked={filters.cancelAtPeriodEnd === value}
                    onCheckedChange={() =>
                      onFiltersChange(
                        withFilter(
                          filters,
                          'cancelAtPeriodEnd',
                          filters.cancelAtPeriodEnd === value ? undefined : value,
                        ),
                      )
                    }
                  />
                  <span className="text-sm">
                    {t(`filters.cancelAtPeriodEndOptions.${value ? 'yes' : 'no'}`)}
                  </span>
                </label>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Payment Type */}
        {options?.paymentTypes && options.paymentTypes.length > 0 && (
          <AccordionItem value="paymentType">
            <AccordionTrigger className="py-3 text-sm">
              <div className="flex items-center gap-2">
                {t('filters.paymentType')}
                {getSelectedCount('paymentType') > 0 && (
                  <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                    {getSelectedCount('paymentType')}
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {options.paymentTypes.map((type) => (
                  <label
                    key={type}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <Checkbox
                      checked={filters.paymentType?.includes(type) || false}
                      onCheckedChange={() => toggleArrayValue('paymentType', type)}
                    />
                    <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Billing Cycle (Chain Index) */}
        <AccordionItem value="chainIndex">
          <AccordionTrigger className="py-3 text-sm">
            <div className="flex items-center gap-2">
              {t('filters.billingCycle')}
              {filters.chainIndexValues && filters.chainIndexValues.length > 0 && (
                <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  {filters.chainIndexValues.length}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-wrap gap-1.5">
              {CYCLE_OPTIONS.map((opt) => {
                const isSelected = filters.chainIndexValues?.includes(opt.value as any) || false;
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => {
                      const current = filters.chainIndexValues || [];
                      const updated = isSelected
                        ? current.filter((v) => v !== opt.value)
                        : [...current, opt.value];
                      onFiltersChange(
                        withFilter(filters, 'chainIndexValues', updated as OperationFilters['chainIndexValues']),
                      );
                    }}
                    className={cn(
                      'px-3 py-1 text-sm rounded-md border transition-colors',
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-muted border-input'
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Retry Count */}
        <AccordionItem value="retryCount">
          <AccordionTrigger className="py-3 text-sm">
            <div className="flex items-center gap-2">
              {t('filters.retryCount')}
              {filters.retryCountValues && filters.retryCountValues.length > 0 && (
                <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  {filters.retryCountValues.length}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-wrap gap-1.5">
              {CYCLE_OPTIONS.map((opt) => {
                const isSelected = filters.retryCountValues?.includes(opt.value as any) || false;
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => {
                      const current = filters.retryCountValues || [];
                      const updated = isSelected
                        ? current.filter((v) => v !== opt.value)
                        : [...current, opt.value];
                      onFiltersChange(
                        withFilter(filters, 'retryCountValues', updated as OperationFilters['retryCountValues']),
                      );
                    }}
                    className={cn(
                      'px-3 py-1 text-sm rounded-md border transition-colors',
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background hover:bg-muted border-input'
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Failure Reason */}
        <AccordionItem value="failureReason">
          <AccordionTrigger className="py-3 text-sm">
            <div className="flex items-center gap-2">
              {t('filters.failureReason')}
              {getSelectedCount('lastFailureReason') > 0 && (
                <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  {getSelectedCount('lastFailureReason')}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <select
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              value={filters.lastFailureReason ?? ''}
              onChange={(e) =>
                onFiltersChange(withFilter(filters, 'lastFailureReason', e.target.value || undefined))
              }
            >
              <option value="">{t('filters.anyFailureReason')}</option>
              {(options?.failureReasons ?? []).map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </AccordionContent>
        </AccordionItem>

        {/* Created at */}
        <AccordionItem value="createdAt">
          <AccordionTrigger className="py-3 text-sm">
            <div className="flex items-center gap-2">
              {t('filters.createdAt')}
              {getSelectedCount('createdAt') > 0 && (
                <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  1
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <DateRangeFilter
              value={filters.createdAt}
              onChange={(next) => onFiltersChange(withFilter(filters, 'createdAt', next))}
              fromLabel={t('filters.dateFrom')}
              toLabel={t('filters.dateTo')}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Next billing date */}
        <AccordionItem value="nextBillingDate">
          <AccordionTrigger className="py-3 text-sm">
            <div className="flex items-center gap-2">
              {t('filters.nextBillingDate')}
              {getSelectedCount('nextBillingDate') > 0 && (
                <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                  1
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <DateRangeFilter
              value={filters.nextBillingDate}
              onChange={(next) => onFiltersChange(withFilter(filters, 'nextBillingDate', next))}
              fromLabel={t('filters.dateFrom')}
              toLabel={t('filters.dateTo')}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

type DateFilterValue = OperationFilters['createdAt'];

function DateRangeFilter({
  value,
  onChange,
  fromLabel,
  toLabel,
}: {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
  fromLabel: string;
  toLabel: string;
}) {
  const [from, to] = value?.value ?? [];

  const emit = (nextFrom?: string, nextTo?: string) => {
    if (!nextFrom && !nextTo) {
      onChange(undefined);
      return;
    }
    if (nextFrom && nextTo) {
      onChange({ condition: 'is-between-date', value: [nextFrom, nextTo] });
      return;
    }
    onChange(
      nextFrom
        ? { condition: 'is-after-date', value: [nextFrom] }
        : { condition: 'is-before-date', value: [nextTo!] }
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-normal">{fromLabel}</Label>
      <Input type="date" value={from ?? ''} onChange={(e) => emit(e.target.value || undefined, to)} />
      <Label className="text-xs font-normal">{toLabel}</Label>
      <Input type="date" value={to ?? ''} onChange={(e) => emit(from, e.target.value || undefined)} />
    </div>
  );
}
